import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import * as R from 'ramda';
import { OrderDetailsExtended } from '../lib/order-details-extended';
import { getProductVariantIdsFromOrderItems } from '../lib/helpers/getProductVariantIdsFromOrderItems';
import { EProductSearchRequest } from '@gelato-api-ui/core/e-commerce/e-product-search-request';
import { EProductSearchResponse } from '@gelato-api-ui/core/e-commerce/e-product-search-response';
import { EProductVariantSearchRequest } from '@gelato-api-ui/core/e-commerce/e-product-variant-search-request';
import { EProductVariant } from '@gelato-api-ui/core/e-commerce/e-product-variant';
import { EProductAbbridged } from '@gelato-api-ui/core/e-commerce/e-product-abbridged';
import { ECommerceProductsApiService } from '@gelato-api-ui/core/e-commerce/services/e-commerce-products-api.service';
import { ECommerceProductGraphQlService } from '@gelato-api-ui/core/e-commerce/services/e-commerce-product-graph-ql.service';
import { OrderGraphQlService } from '@api-ui-app/src/app/orders/services/order-graph-ql.service';

interface VariantsAndProducts {
  products: EProductAbbridged[];
  productVariants: EProductVariant[];
}

@Injectable({
  providedIn: 'root',
})
export class OrderDetailsService {
  constructor(
    private readonly orderGraphQlService: OrderGraphQlService,
    private readonly eCommerceProductsApiService: ECommerceProductsApiService,
    private readonly eCommerceProductGraphQlService: ECommerceProductGraphQlService,
  ) {}

  get(orderId: string): Observable<OrderDetailsExtended> {
    return this.orderGraphQlService.get(orderId, true).pipe(
      switchMap((orderDetails): Observable<OrderDetailsExtended> => {
        if (!orderDetails) {
          return of(null);
        }

        const productVariantIds: string[] = getProductVariantIdsFromOrderItems(orderDetails.items);

        if (!productVariantIds.length) {
          return of({
            ...orderDetails,
            products: [],
            productVariants: [],
          });
        }

        return this.getVariantsAndProducts(productVariantIds).pipe(
          map(({ products, productVariants }): OrderDetailsExtended => {
            return {
              ...orderDetails,
              products,
              productVariants,
            };
          }),
        );
      }),
    );
  }

  private getVariantsAndProducts(productVariantIds: string[]): Observable<VariantsAndProducts> {
    return this.getProductVariants(productVariantIds).pipe(
      switchMap((productVariants: EProductVariant[]) => {
        const productIds = R.map(
          (loopProductVariant: EProductVariant): string => loopProductVariant.productId,
          productVariants,
        );

        return this.getProducts(R.uniq(productIds)).pipe(
          map((products: EProductAbbridged[]): VariantsAndProducts => {
            return { productVariants, products };
          }),
        );
      }),
    );
  }

  private getProducts(productIds: string[]): Observable<EProductAbbridged[]> {
    if (!productIds.length) {
      return of([]);
    }

    const searchRequest: EProductSearchRequest = {
      ids: productIds,
      storeId: '*',
      connectionStatus: undefined,
      limit: 500,
      offset: 0,
    };

    return this.eCommerceProductsApiService.search(searchRequest).pipe(
      catchError(() => {
        return of(null);
      }),
      map((searchResponse: EProductSearchResponse): EProductAbbridged[] => {
        if (!searchResponse || !searchResponse.products) {
          return [];
        }

        return searchResponse.products;
      }),
    );
  }

  private getProductVariants(productVariantIds: string[]): Observable<EProductVariant[]> {
    if (!productVariantIds.length) {
      return of([]);
    }

    const searchRequest: EProductVariantSearchRequest = {
      ids: productVariantIds,
      connectionStatuses: undefined,
      limit: 1500,
      offset: 0,
    };

    return this.eCommerceProductGraphQlService.searchProductVariants('*', searchRequest).pipe(
      catchError(() => {
        return of(null);
      }),
      map((productVariants: EProductVariant[]): EProductVariant[] => productVariants || []),
    );
  }
}
